import { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  Box,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
} from "@material-ui/core";
import {
  BarChart as BarChartIcon,
  ShowChart as LineChartIcon,
  TableChart as TableIcon,
} from "@material-ui/icons";
import api from "../../api";
import useStyles from "./useStyles";
import BarChartView from "./BarChartView";
import LineChartView from "./LineChartView";
import TableChartView from "./TableChartView";
import TopPartnersTable from "./TopPartnersTable";
import {
  COLORS,
  FORECAST_COLOR,
  currentMonthStr,
  getAllCategories,
  getEffectiveCents,
  stripCategoryPrefix,
} from "./utils";

export default function BillingPanel() {
  const classes = useStyles();
  const [monthlyData, setMonthlyData] = useState([]);
  const [topPartners, setTopPartners] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(currentMonthStr());
  const [months, setMonths] = useState(3);
  const [viewMode, setViewMode] = useState("bar");
  const [loading, setLoading] = useState(true);
  const [partnersLoading, setPartnersLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api
      .getBoomReportCharges({ months, topPartnersMonth: selectedMonth })
      .then(api.pickData)
      .then((data) => {
        setMonthlyData(data?.months || []);
        setTopPartners(data?.topPartners || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [months]);

  function handleSelectMonth(month) {
    if (month === selectedMonth) return;
    setSelectedMonth(month);
    setPartnersLoading(true);
    api
      .getBoomReportCharges({ months, topPartnersMonth: month })
      .then(api.pickData)
      .then((data) => {
        setTopPartners(data?.topPartners || []);
        setPartnersLoading(false);
      })
      .catch(() => setPartnersLoading(false));
  }

  const allCategories = getAllCategories(monthlyData);
  const colorMap = {};
  allCategories.forEach((cat, i) => {
    colorMap[cat] = COLORS[i % COLORS.length];
  });

  const maxCents = Math.max(...monthlyData.map((item) => getEffectiveCents(item)), 0);

  if (loading) return <Typography>Loading...</Typography>;
  if (monthlyData.length === 0) return <Typography>No charges found.</Typography>;

  return (
    <>
      <Paper className={classes.chartSection} style={{ padding: 24 }}>
        <Box className={classes.chartHeader}>
          <Typography variant="h6">Monthly Charges</Typography>
          <Box className={classes.chartControls}>
            <FormControl variant="outlined" size="small" style={{ minWidth: 110 }}>
              <InputLabel>Months</InputLabel>
              <Select
                value={months}
                onChange={(e) => setMonths(e.target.value)}
                label="Months"
              >
                <MenuItem value={3}>3 months</MenuItem>
                <MenuItem value={6}>6 months</MenuItem>
                <MenuItem value={12}>12 months</MenuItem>
              </Select>
            </FormControl>
            <Box className={classes.viewToggle}>
              <IconButton
                size="small"
                className={`${classes.viewButton} ${viewMode === "bar" ? classes.viewButtonActive : ""}`}
                onClick={() => setViewMode("bar")}
              >
                <BarChartIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                className={`${classes.viewButton} ${viewMode === "line" ? classes.viewButtonActive : ""}`}
                onClick={() => setViewMode("line")}
              >
                <LineChartIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                className={`${classes.viewButton} ${viewMode === "table" ? classes.viewButtonActive : ""}`}
                onClick={() => setViewMode("table")}
              >
                <TableIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Box>

        {viewMode !== "table" && (
          <Box display="flex" flexWrap="wrap" mb={2}>
            {allCategories.map((cat) => (
              <Box key={cat} className={classes.legendItem}>
                <Box className={classes.legendSwatch} style={{ backgroundColor: colorMap[cat] }} />
                <Typography variant="caption">
                  {stripCategoryPrefix(cat)}
                </Typography>
              </Box>
            ))}
            {monthlyData.some((item) => item.forecast) && (
              <Box className={classes.legendItem}>
                <Box
                  className={classes.legendSwatch}
                  style={{
                    background: `repeating-linear-gradient(45deg, ${FORECAST_COLOR}, ${FORECAST_COLOR} 2px, transparent 2px, transparent 4px)`,
                  }}
                />
                <Typography variant="caption">forecast (unbilled)</Typography>
              </Box>
            )}
          </Box>
        )}

        {viewMode === "bar" && (
          <BarChartView
            data={monthlyData}
            maxCents={maxCents}
            categories={allCategories}
            colorMap={colorMap}
            classes={classes}
            selectedMonth={selectedMonth}
            onSelect={handleSelectMonth}
          />
        )}
        {viewMode === "line" && (
          <LineChartView
            data={monthlyData}
            categories={allCategories}
            colorMap={colorMap}
            classes={classes}
            selectedMonth={selectedMonth}
            onSelect={handleSelectMonth}
          />
        )}
        {viewMode === "table" && (
          <TableChartView
            data={monthlyData}
            classes={classes}
            selectedMonth={selectedMonth}
            onSelect={handleSelectMonth}
          />
        )}
      </Paper>

      <TopPartnersTable
        topPartners={topPartners}
        selectedMonth={selectedMonth}
        loading={partnersLoading}
        classes={classes}
      />
    </>
  );
}
